import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { requetes, type VuePieces, type VueTaches } from '../api'
import { Icone } from './Icone'

// Parcourir une liste (tâches, pièces) une à une depuis la fiche : précédente et suivante
// (boutons, flèches du clavier, glissement sur téléphone). L'ordre est figé à l'entrée
// dans la file, pour qu'un objet traité, sorti de la liste, ne décale pas la suite.

type Cle = `taches:${VueTaches}` | `pieces:${VuePieces}`

const files = new Map<Cle, string[]>()

/** Ordre de la liste affichée : la page de liste le fixe à chaque affichage. */
export function retenirFile(cle: Cle, liste: readonly { id: string }[]) {
  files.set(
    cle,
    liste.map((o) => o.id),
  )
}

export type FileParcours = {
  position: number
  total: number
  precedente: string | null
  suivante: string | null
  aller: (id: string) => void
}

/** La file retenue par la liste, sinon celle relue, si elle contient l'objet affiché. */
function useFile(
  cle: Cle,
  id: string,
  relue: readonly { id: string }[] | undefined,
  aller: (id: string) => void,
): FileParcours | null {
  const retenue = files.get(cle)
  let ids = retenue?.includes(id) ? retenue : null
  if (!ids && relue?.some((o) => o.id === id)) ids = relue.map((o) => o.id)
  useEffect(() => {
    if (ids && ids !== retenue) files.set(cle, ids)
  }, [ids, retenue, cle])
  if (!ids || ids.length < 2) return null
  const i = ids.indexOf(id)
  return {
    position: i + 1,
    total: ids.length,
    precedente: ids[i - 1] ?? null,
    suivante: ids[i + 1] ?? null,
    aller,
  }
}

export function useFileTaches(id: string, vue: VueTaches): FileParcours | null {
  const navigate = useNavigate()
  const liste = useQuery(requetes.taches({ vue }))
  return useFile(`taches:${vue}`, id, liste.data, (cible) => {
    void navigate({ to: '/taches/$id', params: { id: cible }, search: { vue } })
  })
}

/** Sans vue (pièce ouverte depuis une échéance, une tâche…), pas de file. */
export function useFilePieces(id: string, vue: VuePieces | undefined): FileParcours | null {
  const navigate = useNavigate()
  const liste = useQuery({ ...requetes.pieces({ vue: vue ?? 'recentes' }), enabled: !!vue })
  const file = useFile(`pieces:${vue ?? 'recentes'}`, id, liste.data, (cible) => {
    void navigate({ to: '/pieces/$id', params: { id: cible }, search: { vue } })
  })
  return vue ? file : null
}

const majuscule = (mot: string) => mot.charAt(0).toUpperCase() + mot.slice(1)

function saisieEnCours(cible: EventTarget | null): boolean {
  return (
    cible instanceof HTMLElement &&
    (cible.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(cible.tagName))
  )
}

/** Barre « précédente · n / total · suivante », avec clavier et glissement. */
export function NavigationFile({ file, objet }: { file: FileParcours; objet: 'tâche' | 'pièce' }) {
  const { precedente, suivante, aller } = file
  const depart = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    function touche(e: KeyboardEvent) {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || saisieEnCours(e.target)) return
      if (document.querySelector('dialog[open]')) return
      if (e.key === 'ArrowLeft' && precedente) aller(precedente)
      if (e.key === 'ArrowRight' && suivante) aller(suivante)
    }
    // Glissement horizontal franc, hors des zones qui défilent elles-mêmes.
    function debut(e: TouchEvent) {
      const cible = e.target instanceof Element ? e.target : null
      const t = e.touches[0]
      depart.current =
        e.touches.length === 1 && t && !cible?.closest('input, textarea, select, dialog, canvas')
          ? { x: t.clientX, y: t.clientY }
          : null
    }
    function fin(e: TouchEvent) {
      const d = depart.current
      const t = e.changedTouches[0]
      depart.current = null
      if (!d || !t) return
      const dx = t.clientX - d.x
      const dy = t.clientY - d.y
      if (Math.abs(dx) < 70 || Math.abs(dy) > Math.abs(dx) / 2) return
      if (dx > 0 && precedente) aller(precedente)
      if (dx < 0 && suivante) aller(suivante)
    }
    window.addEventListener('keydown', touche)
    window.addEventListener('touchstart', debut, { passive: true })
    window.addEventListener('touchend', fin)
    return () => {
      window.removeEventListener('keydown', touche)
      window.removeEventListener('touchstart', debut)
      window.removeEventListener('touchend', fin)
    }
  }, [precedente, suivante, aller])

  return (
    <nav className="navigation-file" aria-label={`Parcours des ${objet}s`}>
      <button
        type="button"
        className="bouton-discret"
        disabled={!precedente}
        onClick={() => precedente && aller(precedente)}
        title={`${majuscule(objet)} précédente (flèche gauche)`}
      >
        <Icone nom="chevron_left" />
        <span>Précédente</span>
      </button>
      <span className="mono discret">
        {file.position} / {file.total}
      </span>
      <button
        type="button"
        className="bouton-discret"
        disabled={!suivante}
        onClick={() => suivante && aller(suivante)}
        title={`${majuscule(objet)} suivante (flèche droite)`}
      >
        <span>Suivante</span>
        <Icone nom="chevron_right" />
      </button>
    </nav>
  )
}
